/**
 * Forma dei dati di giocatori e rose, con ordinamento e filtri degli elenchi.
 * I tipi rispecchiano le risposte di `apps/api/src/servizi/giocatori.ts` e
 * `rose.ts`. I filtri stanno nel client: l'elenco e gia ristretto dal server
 * a cio che l'utente puo vedere, e resta nell'ordine delle migliaia.
 */

import type { SquadraStagione } from '../navigazione/contesto.js';
import {
  ETICHETTA_RUOLO_GIOCATORE,
  FAMIGLIE_ATTRIBUTO,
  RUOLI_GIOCATORE,
  filtriSchedaSchema,
  type FamigliaAttributo,
  type RuoloGiocatore,
  type StatoGiocatore,
  type StatoSportivo,
} from '@calcio/shared';

export type Piede = 'destro' | 'sinistro' | 'ambidestro';

/** Diffida o squalifica in una competizione; chi non ne ha e regolare. */
export interface StatoSportivoInCompetizione {
  competizioneId: string;
  competizione: string;
  stato: StatoSportivo;
}

export interface Giocatore {
  id: string;
  nome: string;
  cognome: string;
  dataNascita: string | null;
  piede: Piede | null;
  ruoloPreferito: RuoloGiocatore;
  stato: StatoGiocatore;
  statiSportivi: StatoSportivoInCompetizione[];
  archiviato: boolean;
  squadre: { id: string; nome: string }[];
}

/** Scheda giocatore (V36): anagrafica, stati, note e flag di scrittura degli stati. */
export interface DettaglioGiocatore {
  id: string;
  nome: string;
  cognome: string;
  dataNascita: string | null;
  piede: Piede | null;
  ruoloPreferito: RuoloGiocatore;
  stato: StatoGiocatore;
  statiSportivi: StatoSportivoInCompetizione[];
  note: string | null;
  /** Le attive, fra cui scegliere diffida o squalifica. */
  competizioniAttive: { id: string; nome: string }[];
  archiviato: boolean;
  puoScrivereStati: boolean;
  /** Del tesseramento in corso nella stagione corrente; null senza. */
  numeroMaglia: number | null;
  /** Come nella rosa; assente per chi non legge le misure. */
  medieAttributi?: MedieAttributi;
}

/** Riga dello storico tesseramenti (V37). */
export interface TesseramentoStorico {
  id: string;
  squadraStagioneId: string;
  squadra: string;
  stagione: string;
  numeroMaglia: number | null;
  dataInizio: string;
  dataFine: string | null;
}

export interface RigaRosa {
  tesseramentoId: string;
  giocatoreId: string;
  numeroMaglia: number | null;
  nome: string;
  cognome: string;
  dataNascita: string | null;
  piede: Piede | null;
  ruoloPreferito: RuoloGiocatore;
  stato: StatoGiocatore;
  statiSportivi: StatoSportivoInCompetizione[];
  dataInizio: string;
  dataFine: string | null;
  minuti: number;
  /** Assente per chi non legge i voti. */
  votoMedio?: number | null;
  /** Ultimo valore di ogni attributo, mediato per famiglia; assente per chi non legge le misure. */
  medieAttributi?: MedieAttributi;
}

export interface MedieAttributi {
  /** Media delle medie di famiglia, ognuna con lo stesso peso. */
  overall: number | null;
  fisici: number | null;
  tecnici: number | null;
  mentali: number | null;
  portiere: number | null;
}

/** Le medie di famiglia, le stesse per tutti i ruoli, nell'ordine del catalogo. */
export type FamigliaMedia = Exclude<keyof MedieAttributi, 'overall'>;
export const FAMIGLIE_MEDIA: readonly FamigliaMedia[] = FAMIGLIE_ATTRIBUTO;

/** Riga dell'anteprima di import (V09). */
export interface RigaImport {
  giocatoreId: string;
  nome: string;
  cognome: string;
  dataNascita: string | null;
  numeroMaglia: number | null;
  dataFine: string | null;
  escluso: 'gia_in_rosa' | 'archiviato' | null;
}

/**
 * Il ruolo e uno dei quattro valori: portiere, difensore, centrocampista,
 * attaccante, ed e obbligatorio. L'ordine dei reparti segue quello dell'enum.
 */
export const REPARTI = ['Portieri', 'Difensori', 'Centrocampisti', 'Attaccanti'] as const;

export function repartoDi(ruolo: RuoloGiocatore): number {
  return RUOLI_GIOCATORE.indexOf(ruolo);
}

/** La sigla del ruolo sulla fascia della maglia. */
export const SIGLA_RUOLO: Record<RuoloGiocatore, string> = {
  portiere: 'POR',
  difensore: 'DIF',
  centrocampista: 'CEN',
  attaccante: 'ATT',
};

export function etichettaRuolo(ruolo: RuoloGiocatore): string {
  return ETICHETTA_RUOLO_GIOCATORE[ruolo];
}

/** La rosa divisa nei quattro reparti, dal portiere all'attaccante. */
export function perReparto<R extends Pick<RigaRosa, 'ruoloPreferito'>>(righe: readonly R[]): R[][] {
  return RUOLI_GIOCATORE.map((ruolo) => righe.filter((r) => r.ruoloPreferito === ruolo));
}

/** La riga con le medie al primo livello, per ordinarla sulle colonne della tabella. */
export type RigaRosaOrdinabile = RigaRosa & MedieAttributi;

export function conMedie(r: RigaRosa): RigaRosaOrdinabile {
  const m = r.medieAttributi;
  return {
    ...r,
    overall: m?.overall ?? null,
    fisici: m?.fisici ?? null,
    tecnici: m?.tecnici ?? null,
    mentali: m?.mentali ?? null,
    portiere: m?.portiere ?? null,
  };
}

/** La data di fine e l'ultimo giorno in rosa: esce chi l'ha prima di oggi. */
export function dividiRosa(righe: readonly RigaRosa[], oggi: string) {
  const uscito = (r: RigaRosa) => r.dataFine !== null && r.dataFine < oggi;
  return { inRosa: righe.filter((r) => !uscito(r)), usciti: righe.filter(uscito) };
}

/**
 * Spuntati in partenza: chi e ancora nella rosa di origine e si puo importare.
 * Chi ne e uscito resta in elenco, da spuntare a mano se torna.
 */
export function preselezioneImport(righe: readonly RigaImport[], oggi: string): Set<string> {
  return new Set(
    righe
      .filter((r) => r.escluso === null && (r.dataFine === null || r.dataFine >= oggi))
      .map((r) => r.giocatoreId),
  );
}

/** Rose da cui importare, per stagione: l'elenco arriva gia con la corrente in testa. */
export function originiImport(
  squadre: readonly SquadraStagione[],
  destinazione: string,
): [string, SquadraStagione[]][] {
  const perStagione = new Map<string, SquadraStagione[]>();
  for (const s of squadre) {
    if (s.id === destinazione) continue;
    perStagione.set(s.stagione, [...(perStagione.get(s.stagione) ?? []), s]);
  }
  return [...perStagione];
}

export interface Filtri {
  testo: string;
  /** Id della squadra-stagione, oppure 'tutte' o 'nessuna'. */
  squadra: string;
  stato: 'tutti' | 'non_disponibili' | 'diffidati' | 'squalificati';
}

const normalizza = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export function filtraGiocatori(elenco: readonly Giocatore[], filtri: Filtri): Giocatore[] {
  const parole = normalizza(filtri.testo).split(/\s+/).filter(Boolean);
  return elenco.filter((g) => {
    const nome = normalizza(`${g.nome} ${g.cognome}`);
    if (!parole.every((p) => nome.includes(p))) return false;
    if (filtri.squadra === 'nessuna' && g.squadre.length > 0) return false;
    if (
      filtri.squadra !== 'tutte' &&
      filtri.squadra !== 'nessuna' &&
      !g.squadre.some((s) => s.id === filtri.squadra)
    ) {
      return false;
    }
    if (filtri.stato === 'non_disponibili') return g.stato !== 'disponibile';
    if (filtri.stato === 'diffidati') return g.statiSportivi.some((s) => s.stato === 'diffidato');
    if (filtri.stato === 'squalificati') {
      return g.statiSportivi.some((s) => s.stato === 'squalificato');
    }
    return true;
  });
}

export const annoDi = (data: string | null): string => data?.slice(0, 4) ?? '—';

/** Oggi nel giorno italiano, come "YYYY-MM-DD". */
export { oggi } from '../comuni/date.js';

/** La scala dei voti, da 1 a 10 a mezzi punti. */
export const VALORI_SCALA = Array.from({ length: 19 }, (_, i) => 1 + i * 0.5);

export type FiltriScheda = {
  stagione?: string;
  da?: string;
  a?: string;
  competizione?: string;
  squadraStagione?: string;
};

const CHIAVI_FILTRO = ['stagione', 'da', 'a', 'competizione', 'squadraStagione'] as const;

/**
 * I filtri della query string. Un valore non valido (un link vecchio o
 * scritto a mano, come `?competizione=x`) si ignora e gli altri restano: la
 * scheda si apre senza quel filtro invece di rompersi.
 */
export function filtriDaSearch(search: string): FiltriScheda {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const grezzi: Record<string, string> = {
    stagione: q.get('stagione') ?? '',
    da: q.get('da') ?? '',
    a: q.get('a') ?? '',
    competizione: q.get('competizione') ?? '',
    squadraStagione: q.get('squadraStagione') ?? '',
  };
  const esito = filtriSchedaSchema.safeParse(grezzi);
  if (esito.success) return esito.data;
  for (const problema of esito.error.issues) {
    const campo = problema.path[0];
    if (typeof campo === 'string') grezzi[campo] = '';
  }
  return filtriSchedaSchema.parse(grezzi);
}

export function percorsoConFiltri(percorso: string, filtri: FiltriScheda): string {
  const q = new URLSearchParams();
  for (const k of CHIAVI_FILTRO) {
    const v = filtri[k];
    if (v) q.set(k, v);
  }
  const coda = q.toString();
  return coda ? `${percorso}?${coda}` : percorso;
}

export function queryFiltri(filtri: FiltriScheda): string {
  const q = new URLSearchParams();
  for (const k of CHIAVI_FILTRO) {
    const v = filtri[k];
    if (v) q.set(k, v);
  }
  const coda = q.toString();
  return coda ? `?${coda}` : '';
}

export interface FiltriDisponibili {
  stagioni: { id: string; nome: string }[];
  competizioni: { id: string; nome: string }[];
  squadreStagione: { id: string; squadra: string; stagione: string }[];
}

/** Da quale esecuzione di un test nasce una misurazione. */
export interface ProvenienzaMisurazione {
  id: string;
  squadraStagioneId: string;
  testId: string;
  test: string;
  /** La misura del test da cui nasce: ha il nome del test se è l'unica. */
  misura: string;
  risultato: Record<string, number>;
  unita: string | null;
  /** Metri (gradini) o tempo netto (penalità); `null` per gli altri metodi. */
  derivato: number | null;
}

export interface MisurazioneRiga {
  id: string;
  valore: number;
  data: string;
  autore: string | null;
  /** Falso per le generate da un test: si correggono dall'esecuzione. */
  puoModificare: boolean;
  esecuzione: ProvenienzaMisurazione | null;
}

export interface AttributoScheda {
  id: string;
  nome: string;
  famiglia: FamigliaAttributo;
  attivo: boolean;
  misurazioni: MisurazioneRiga[];
}

export interface AttributiGiocatore {
  puoScrivere: boolean;
  attributi: AttributoScheda[];
  filtriDisponibili: FiltriDisponibili;
}

export interface VotoRiga {
  id: string;
  valore: number;
  data: string;
  tipo: 'partita' | 'allenamento';
  partitaId: string | null;
  allenamentoId: string | null;
  squadraStagioneId: string;
  squadra: string;
  stagione: string;
  avversario: string | null;
  minuti: number | null;
  modificabile: boolean;
}

export interface PartitaSenzaVoto {
  id: string;
  data: string;
  avversario: string;
  squadra: string;
  minuti: number | null;
}

export interface VotiGiocatore {
  voti: VotoRiga[];
  partiteSenzaVoto: PartitaSenzaVoto[];
  filtriDisponibili: FiltriDisponibili;
}

export interface Metriche {
  presenze: number;
  minuti: number;
  gol: number;
  autogol: number;
  assist: number;
  tiriSpecchio: number;
  tiriFuori: number;
  passaggiCompletati: number;
  passaggiSbagliati: number;
  palleConquistate: number;
  pallePerse: number;
  gialli: number;
  rossi: number;
  votoMedio?: number | null;
}

export interface RigaStatistiche extends Metriche {
  squadraStagioneId: string;
  squadra: string;
  stagione: string;
}

export interface StatisticheGiocatore {
  totale: Metriche;
  perSquadra: RigaStatistiche[];
  filtriDisponibili: FiltriDisponibili;
}
